import React, { useState, useEffect, useRef } from "react";
import T from "../styles/tokens";
import { Btn, Ic, Toast } from "../components/UI";
import * as api from "../utils/api";

const TEMPLATE_ICONS = { full_admin: "🔐", helpdesk: "🎧", security_audit: "🛡️", custom: "⚙️" };

export default function NewInvitation() {
  const [step, setStep] = useState(1); // 1: form, 2: review, 3: done
  const [templates, setTemplates] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [pathfinderUsers, setPathfinderUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersOpen, setUsersOpen] = useState(false);
  const [form, setForm] = useState({ clientName: "", contactName: "", contactEmail: "", templateId: "", customRoles: [], selectedEmails: [] });
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [toast, setToast] = useState(null);
  const usersRef = useRef(null);

  useEffect(() => {
    if (!usersOpen) return;
    const onDocClick = (e) => {
      if (usersRef.current && !usersRef.current.contains(e.target)) {
        setUsersOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [usersOpen]);

  useEffect(() => {
    api.listTemplates().then(setTemplates).catch(console.error);
    api.listAvailableRoles().then(setAvailableRoles).catch(console.error);
    api.listPathfinderUsers()
      .then(setPathfinderUsers)
      .catch((err) => setToast({ message: `Failed to load users: ${err.message}`, type: "error" }))
      .finally(() => setUsersLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEmail = (email) => setForm((f) => ({
    ...f,
    selectedEmails: f.selectedEmails.includes(email)
      ? f.selectedEmails.filter((e) => e !== email)
      : [...f.selectedEmails, email],
  }));
  const toggleRole = (role) => setForm((f) => ({
    ...f,
    customRoles: f.customRoles.includes(role)
      ? f.customRoles.filter((r) => r !== role)
      : [...f.customRoles, role],
  }));
  const isCustom = form.templateId === "custom";
  const customRolesValid = !isCustom || form.customRoles.length > 0;
  const canProceed = form.clientName && form.contactName && form.contactEmail && form.templateId && customRolesValid && form.selectedEmails.length > 0;
  const selectedTemplate = templates.find((t) => t.id === form.templateId);
  const selectedUsers = pathfinderUsers.filter((u) => form.selectedEmails.includes(u.email));

  const handleCreate = async () => {
    setSending(true);
    try {
      const res = await api.createInvitation({
        clientName: form.clientName,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        templateId: form.templateId,
        customRoles: isCustom ? form.customRoles : undefined,
        pathfinderUsers: form.selectedEmails,
      });
      setResult(res);
      setStep(3);
    } catch (err) {
      setToast({ message: err.message, type: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    if (result?.invitationUrl) {
      navigator.clipboard.writeText(result.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleReset = () => {
    setStep(1);
    setForm({ clientName: "", contactName: "", contactEmail: "", templateId: "", customRoles: [], selectedEmails: [] });
    setResult(null);
    setEmailSent(false);
  };

  const handleSendEmail = async () => {
    if (!result?.invitationId) return;
    setEmailSending(true);
    try {
      const res = await api.sendInvitationEmail(result.invitationId);
      setEmailSent(true);
      setToast({ message: `Email sent to ${res.sentTo}`, type: "success" });
      setTimeout(() => setEmailSent(false), 6000);
    } catch (err) {
      setToast({ message: `Failed to send email: ${err.message}`, type: "error" });
    } finally {
      setEmailSending(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", borderRadius: T.radiusSm,
    border: `1px solid ${T.gray200}`, fontSize: 14, fontFamily: T.font,
    outline: "none", transition: "border-color 0.15s",
    color: T.charcoal,
  };

  const labelStyle = {
    display: "block", fontSize: 12, fontWeight: 600, color: T.indigo,
    marginBottom: 6, fontFamily: T.font,
  };

  return (
    <div style={{ animation: "pfFadeIn 0.3s ease-out", maxWidth: 680 }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: "0 0 6px" }}>New Invitation</h1>
      <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginBottom: 24 }}>Generate a secure onboarding link for a client</p>

      {/* Step indicator */}
      <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
        {[1, 2, 3].map((s) => (
          <div key={s} style={{
            flex: 1, height: 4, borderRadius: 99,
            background: step >= s ? T.purple : T.gray200,
            transition: "background 0.3s",
          }} />
        ))}
      </div>

      {/* Step 1: Form */}
      {step === 1 && (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 28 }}>
          <div style={{ display: "grid", gap: 18 }}>
            <div>
              <label style={labelStyle}>Company Name</label>
              <input style={inputStyle} placeholder="e.g. Acme Corp" value={form.clientName} onChange={(e) => set("clientName", e.target.value)} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input style={inputStyle} placeholder="e.g. Jane Smith" value={form.contactName} onChange={(e) => set("contactName", e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Contact Email</label>
                <input style={inputStyle} placeholder="admin@company.com" value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} />
              </div>
            </div>

            {/* Template Selection */}
            <div>
              <label style={labelStyle}>Permission Template</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => set("templateId", t.id)}
                    style={{
                      padding: "14px 16px", borderRadius: T.radiusSm, textAlign: "left",
                      border: `2px solid ${form.templateId === t.id ? T.purple : T.gray200}`,
                      background: form.templateId === t.id ? "#F8F5FF" : T.white,
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>{TEMPLATE_ICONS[t.id] || "📋"}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.indigo, fontFamily: T.font }}>{t.name}</div>
                    <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginTop: 2, lineHeight: 1.3 }}>{t.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom roles (only for custom template) */}
            {isCustom && (
              <div>
                <label style={labelStyle}>Custom roles to assign</label>
                {availableRoles.length === 0 ? (
                  <div style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, padding: "10px 0" }}>Loading roles…</div>
                ) : (
                  <>
                    <div style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0,
                      border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm,
                      overflow: "hidden",
                    }}>
                      {availableRoles.map((role, i) => {
                        const checked = form.customRoles.includes(role);
                        const col = i % 2;
                        const row = Math.floor(i / 2);
                        const totalRows = Math.ceil(availableRoles.length / 2);
                        const isLastRow = row === totalRows - 1;
                        return (
                          <label
                            key={role}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "8px 12px",
                              borderBottom: isLastRow ? "none" : `1px solid ${T.gray200}`,
                              borderRight: col === 0 ? `1px solid ${T.gray200}` : "none",
                              cursor: "pointer", fontFamily: T.font,
                              background: checked ? "#F8F5FF" : T.white,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRole(role)}
                              style={{ accentColor: T.purple, cursor: "pointer" }}
                            />
                            <span style={{ fontSize: 13, color: T.charcoal, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{role}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginTop: 6 }}>
                      {form.customRoles.length} role{form.customRoles.length === 1 ? "" : "s"} selected
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Pathfinder users to invite */}
            <div>
              <label style={labelStyle}>Pathfinder users to invite as guests</label>
              <div ref={usersRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setUsersOpen((o) => !o)}
                  disabled={usersLoading || pathfinderUsers.length === 0}
                  style={{
                    ...inputStyle,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    cursor: usersLoading || pathfinderUsers.length === 0 ? "not-allowed" : "pointer",
                    background: T.white,
                    textAlign: "left",
                  }}
                >
                  <span style={{ color: form.selectedEmails.length === 0 ? T.gray400 : T.charcoal }}>
                    {usersLoading
                      ? "Loading users…"
                      : pathfinderUsers.length === 0
                      ? "No users found in the Pathfinder tenant"
                      : form.selectedEmails.length === 0
                      ? "Select Pathfinder users…"
                      : `${form.selectedEmails.length} user${form.selectedEmails.length === 1 ? "" : "s"} selected`}
                  </span>
                  <span style={{ color: T.gray400, fontSize: 10, marginLeft: 8 }}>{usersOpen ? "▲" : "▼"}</span>
                </button>

                {usersOpen && pathfinderUsers.length > 0 && (
                  <div style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    maxHeight: 280,
                    overflowY: "auto",
                    background: T.white,
                    border: `1px solid ${T.gray200}`,
                    borderRadius: T.radiusSm,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    zIndex: 10,
                  }}>
                    {pathfinderUsers.map((u) => {
                      const checked = form.selectedEmails.includes(u.email);
                      return (
                        <label
                          key={u.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 12px",
                            borderBottom: `1px solid ${T.gray200}`,
                            cursor: "pointer", fontFamily: T.font,
                            background: checked ? "#F8F5FF" : T.white,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmail(u.email)}
                            style={{ accentColor: T.purple, cursor: "pointer" }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.displayName}</div>
                            <div style={{ fontSize: 11.5, color: T.gray400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
            <Btn onClick={() => setStep(2)} disabled={!canProceed}>Review & Generate</Btn>
          </div>
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 20 }}>Review Invitation</h3>

          <div style={{ display: "grid", gap: 12, fontSize: 13, fontFamily: T.font, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm }}>
              <span style={{ color: T.gray400 }}>Company</span>
              <span style={{ fontWeight: 600, color: T.indigo }}>{form.clientName}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm }}>
              <span style={{ color: T.gray400 }}>Contact</span>
              <span style={{ fontWeight: 600, color: T.indigo }}>{form.contactName} ({form.contactEmail})</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm }}>
              <span style={{ color: T.gray400 }}>Template</span>
              <span style={{ fontWeight: 600, color: T.indigo }}>{selectedTemplate?.name || form.templateId}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: T.gray100, borderRadius: T.radiusSm }}>
              <span style={{ color: T.gray400 }}>Expiry</span>
              <span style={{ fontWeight: 600, color: T.indigo }}>72 hours from generation</span>
            </div>
          </div>

          {/* Roles that will be assigned */}
          {(() => {
            const rolesToShow = isCustom ? form.customRoles : selectedTemplate?.roles || [];
            if (rolesToShow.length === 0) return null;
            return (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                  Roles to be assigned{isCustom ? " (custom)" : ""}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {rolesToShow.map((r) => (
                    <span key={r} style={{ padding: "3px 8px", borderRadius: 4, background: "#F0EAFF", color: T.purple, fontSize: 11, fontWeight: 500, fontFamily: T.font }}>{r}</span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Pathfinder users to invite as guests */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Pathfinder users to invite ({selectedUsers.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {selectedUsers.map((u) => (
                <div key={u.id} style={{ fontSize: 12, fontFamily: T.font, color: T.charcoal }}>
                  <strong>{u.displayName}</strong> — <span style={{ color: T.gray400 }}>{u.email}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setStep(1)}>Back</Btn>
            <Btn onClick={handleCreate} disabled={sending}>{sending ? "Generating..." : "Generate Link"}</Btn>
          </div>
        </div>
      )}

      {/* Step 3: Generated URL */}
      {step === 3 && result && (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 28 }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E8F8F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: T.green }}>
              <Ic.Check />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Invitation Generated</h3>
            <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 6 }}>Share this link with {form.contactName} at {form.clientName}</p>
          </div>

          {/* URL box */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: T.gray100, borderRadius: T.radiusSm, padding: "10px 14px",
            marginBottom: 12,
          }}>
            <input
              readOnly
              value={result.invitationUrl}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                fontSize: 12, fontFamily: "monospace", color: T.charcoal,
              }}
            />
            <Btn size="sm" variant={copied ? "ghost" : "primary"} onClick={handleCopy} icon={copied ? <Ic.Check /> : <Ic.Copy />}>
              {copied ? "Copied!" : "Copy Link"}
            </Btn>
            <Btn size="sm" variant={emailSent ? "ghost" : "secondary"} onClick={handleSendEmail} disabled={emailSending}>
              {emailSending ? "Sending…" : emailSent ? "Sent ✓" : `Send Email`}
            </Btn>
          </div>
          <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 16 }}>
            Email will be sent to <strong style={{ color: T.charcoal }}>{form.contactEmail}</strong>
          </div>

          <div style={{ fontSize: 12, color: T.gray400, fontFamily: T.font, marginBottom: 20, lineHeight: 1.6 }}>
            This link expires on <strong style={{ color: T.charcoal }}>{new Date(result.expiresAt).toLocaleString()}</strong>. The link is single-use and can only be used once for admin consent.
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <Btn variant="secondary" onClick={handleReset} icon={<Ic.Plus />}>Create Another</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
